import { ref, watch, computed, getCurrentInstance } from "@vue/composition-api";
import axios from "@axios";
import { useToast } from "vue-toastification/composition";
import { debounce } from "lodash";
import store from "@/store";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import ability from "@/libs/acl/ability";
import { EventBus } from "@/libs/event-bus";
import { hasBauComplementsQueryFlag } from "@/utils/bauFormQuery";
import {
  resolveOperationalSecurityFormConfig,
} from "@/config/operationalSecurityForms";

const normalizeUserText = (value) => String(value || "").trim();

const resolveUserDisplayName = (userOption) => {
  const fullName = normalizeUserText(userOption?.full_name);
  if (fullName) return fullName;
  const name = normalizeUserText(userOption?.name);
  return name;
};

const resolveUserIdentifier = (userOption) => {
  const user = normalizeUserText(userOption?.user);
  if (user) return user;
  const username = normalizeUserText(userOption?.username);
  if (username) return username;
  return normalizeUserText(userOption?.email);
};

const resolveUserOptionValue = (userOption) => {
  if (userOption?.id != null) return userOption.id;
  if (userOption?.value != null) return userOption.value;
  return null;
};

const buildUserOptionLabel = (userOption) => {
  const name = resolveUserDisplayName(userOption);
  const user = resolveUserIdentifier(userOption);
  return `${name} | ${user}`;
};

const mapUserOption = (userOption) => {
  const value = resolveUserOptionValue(userOption);
  if (value == null) return null;
  const fullName = resolveUserDisplayName(userOption);
  const user = resolveUserIdentifier(userOption);
  if (!fullName || !user) return null;
  return {
    id: value,
    value,
    label: buildUserOptionLabel(userOption),
    full_name: fullName,
    user,
    email: normalizeUserText(userOption?.email),
  };
};

const mapUserOptions = (users = []) =>
  users.map((item) => mapUserOption(item)).filter(Boolean);

const doesUserOptionMatchSearch = ({ option, search }) => {
  const normalizedSearch = normalizeUserText(search).toLowerCase();
  if (!normalizedSearch) return true;
  const searchableValues = [option?.full_name, option?.user];
  return searchableValues.some((value) =>
    normalizeUserText(value).toLowerCase().includes(normalizedSearch)
  );
};

export default function useEvaluationForm(
  formLocation = "modal",
  formValidationData = null,
  initialTemplateId = null,
  emit
) {
  const totalSteps = 2;
  const defaultReferenceNumber = "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx";
  const currentPage = ref(1);
  const totalList = ref(0);
  const templatesOptions = ref([]);
  const lastPage = ref(null);
  const currentStep = ref(1);
  const templateSelected = ref(null);
  const referenceNumber = ref(``);
  const facilitySelected = ref(null);
  const templatePreview = ref([]);
  const isAnsweringForm = ref(false);
  const facilityName = ref(false);
  const templateName = ref(false);
  const currentFormStatus = ref(null);
  const showActionsButtons = ref(false);
  const dataOnSaveForm = ref(null);
  const disabledUnitSelect = ref(false);
  const isCreate = ref(false);
  const isPrefill = ref(false);
  const userSelected = ref(null);
  const userOptions = ref([]);
  const validatorsOptions = ref([]);
  const validationComment = ref("");
  const formValidationComment = ref("");
  const formValidationRejectedAt = ref("");
  const formValidationAt = ref("");
  const isCreateSpecialForm = ref(false);
  const referenceShowed = ref(null);
  const currentSpecialForm = ref(null);
  const anotherFacility = ref("");
  const isShowAnotherFacility = ref(false);
  const user = store.getters["user/getUser"];
  const reopenedComments = ref(null);
  const reopenedComment = ref("");
  const formTracking = ref([]);
  const isLoadingTracking = ref(false);
  const formHistory = ref([]);
  const isLoadingHistory = ref(false);
  const showReferenceNumber = ref(false);
  const isFormFromSameUser = ref(true);
  const evaluation_id = ref(null);
  const currentEvaluation = ref(null);
  const isFormFromUrl = ref(false);
  const userAssigned = ref(null);
  const isStatusBeingUpdated = ref(false);
  const isBauBatchInProgress = ref(false);
  const buildFormComponent = ref(null);
  const alarmType = ref(null);
  const currentAlarmId = ref(null);
  const ownerOfForm = ref(null);
  const isOwnerOfForm = ref(false);
  const isSignificantEvent = ref(false);
  const significantEventTitle = ref('');
  const significantEventDescription = ref('');
  const significantSecurityManager = ref(null);
  const regionalManagerOptions = ref([]);
  const significantEventId = ref(null);
  const significantEventSecManagerComment = ref('');
  const significantEventSecManagerCommentDate = ref('');
  const canCloseFormManually = ref(false);

  const toast = useToast();
  const { t } = useI18nUtils();
  const vm = getCurrentInstance();
  const proxy = vm.root.proxy;
  const specialForms = {
    SITREP: "SITREP",
    newsletter: "Boletin Informativo",
    news_flash: "Flash informativo",
    informative_email: "Correo informativo",
    BAU: "Atención diaria BAU",
    Incidente: "Atención diaria INCIDENTES",
  };
  const visitsInmueblesTemplateName = "Visitas Inmuebles";

  const translations = {
    "operational_security.evaluation.error_send_form": t(
      "operational_security.evaluation.error_send_form"
    ),
    "operational_security.evaluation.success_send_form": t(
      "operational_security.evaluation.success_send_form"
    ),
    "operational_security.evaluation.answers_successfully": t(
      "operational_security.evaluation.answers_successfully"
    ),
    "operational_security.evaluation.answers_error": t(
      "operational_security.evaluation.answers_error"
    ),
    "operational_security.evaluation.review_successfully": t(
      "operational_security.evaluation.review_successfully"
    ),
    "operational_security.evaluation.review_error": t(
      "operational_security.evaluation.review_error"
    ),
    "operational_security.evaluation.status_successfully": t(
      "operational_security.evaluation.status_successfully"
    ),
    "operational_security.evaluation.status_error": t(
      "operational_security.evaluation.status_error"
    ),
    "operational_security.evaluation.reopen_successfully": t(
      "operational_security.evaluation.reopen_successfully"
    ),
    "operational_security.evaluation.reopen_error": t(
      "operational_security.evaluation.reopen_error"
    ),
    "operational_security.confirmGoBack": t("operational_security.confirmGoBack"),
    "operational_security.confirmGoBackSubtitle": t(
      "operational_security.confirmGoBackSubtitle"
    ),
    "alarm.timeline.yes": t("alarm.timeline.yes"),
    "alarm.timeline.no": t("alarm.timeline.no"),
    "messages.validation.missing_fields.title": t(
      "messages.validation.missing_fields.title"
    ),
    "operational_security.save_comments_success": t(
      "operational_security.save_comments_success"
    ),
  };

  const isUserSupervisor = isSupervisor(user.profile.type);
  const facilityOptions = computed(() => {
    const items = store.state.unit?.items || [];
    if (items.length === 0) {
      return [];
    }
    // const usersTenants = computed(() => user?.tenants.map(t => t.id));
    // return items.filter(u => u.router && usersTenants.value.includes(u.router.id_tenant));
    return items.filter((u) => u.router);
  });
  const formStatus = computed(() => {
    return {
      isValidationProcess: currentFormStatus.value !== null,
      inProgress: currentFormStatus.value === "in_progress",
      isPending: currentFormStatus.value === "pending",
      isReview: currentFormStatus.value === "review",
      isCompleted: currentFormStatus.value === "completed",
      isRejected: currentFormStatus.value === "rejected",
      isReopened: currentFormStatus.value === "reopened",
    };
  });

  const isReadOnlyUser = computed(() =>
    templateSelected.value?.read_only_users?.some((u) => u.id === user.id)
  );

  const isMarkerCheckerFlowAllowed = computed(
    () =>  templateSelected.value?.is_maker_checker
  );

  // For BAU Form
  const hasBauComplementsQuery = computed(() =>
    hasBauComplementsQueryFlag(proxy?.$route?.query || {})
  );
  const isVisitsInmueblesTemplate = computed(() => {
    const selectedTemplateName = templateSelected.value?.name;
    const resolvedTemplateName =
      selectedTemplateName ||
      templateName.value ||
      currentEvaluation.value?.template?.name;
    return resolvedTemplateName === visitsInmueblesTemplateName;
  });

  const canUserSendToReview = computed(() => {
    if (
      !formStatus.value.isReview &&
      !formStatus.value.isCompleted &&
      !formStatus.value.isValidationProcess &&
      isFormFromSameUser.value
    )
      return true;

    if (
      (formStatus.value.isReopened || formStatus.value.isRejected) &&
      cannotModerate() &&
      isFormFromSameUser.value
    )
      return true;

    if (
      (formStatus.value.isReopened || formStatus.value.isRejected) &&
      isMasterAdmOrSuperUser(user.profile.type)
    )
      return true;

    return false;
  });

  // solo cuando este estatus:completado y con permiso de reabrir formulario
  const isOnBauPath = computed(() =>
    hasBauComplementsQuery.value ||
    String(proxy?.$route?.path || "").includes("form-answers-bau")
  );

  const canUserReopenForm = computed(() => {
    if(!formStatus.value.isCompleted) {
      return false;
    }
    if(isOnBauPath.value) {
      return false;
    }
    // se usa "approve" a falta de un permiso de "reopen"
    return ability.can("approve", "OpSecurityFormAnswers");
  });

  const allowedStatuses = computed(() => [
    formStatus.value.isPending,
    formStatus.value.isReview,
    // formStatus.value.isReopened  // validate if this is req
  ]);
  const canRejectForm = computed(() => {
    return (
      ability.can("reject", "OpSecurityFormAnswers") &&
      allowedStatuses.value.some(Boolean) &&
      isAssignedUser.value
    );
  });

  const canApproveForm = computed(() => {
    return (
      ability.can("approve", "OpSecurityFormAnswers") &&
      allowedStatuses.value.some(Boolean) &&
      isAssignedUser.value
    );
  });

  const isAssignedUser = computed(() => userAssigned.value?.id === user?.id);

  function isSupervisor(profile) {
    const p = profile.toLowerCase();
    return p.includes("supervisor") || p.includes("masteradmin");
  }

  function isMasterAdmOrSuperUser(profile) {
    const p = profile.toLowerCase();
    return (
      p.includes("masteradmin") || p.includes("superusuario") || p.includes("supervisor")
    );
  }

  watch([currentPage], () => {
    fetchList();
  });

  // watch([templateSelected], () => {
  //   console.log(templateSelected.value)
  // });

  const clearForm = () => {
    templateSelected.value = null;
    evaluation_id.value = null;
    dataOnSaveForm.value = null;
    isAnsweringForm.value = true;
  };

  const validateIfUrlHasParams = (isFromUrl, params) => {
    if (!isFromUrl) return;
    isFormFromUrl.value = true;
    if (params?.name === "template") {
      templateSelected.value = {};
      templateSelected.value.id = params.value;
      startAnsweringForm();
      return;
    }
    if (params?.name === "edit") {
      evaluation_id.value = params.value;
      startAnsweringForm();
    }
  };

  function cannotModerate() {
    return (
      !ability.can("reject", "OpSecurityFormAnswers") &&
      !ability.can("approve", "OpSecurityFormAnswers")
    );
  }

  const fetchList = (q = "") => {
    const params = {
      q,
      max: 20,
      "page[n]": currentPage.value,
    };

    if (formLocation === "modal" && isCreate.value) {
      params.prefill_template = true;
    }

    if (isPrefill.value) {
      params.requires_prefill = true;
    }
    if (isCreateSpecialForm.value && params.q === "") {
      params.q = specialForms[currentSpecialForm.value] || currentSpecialForm.value;
    }

    axios
      .get("/v1/operational-security-forms/templates", { params })
      .then((response) => {
        const { data } = response.data;
        let filteredData = data;
        totalList.value = response.data.meta.total;
        lastPage.value = response.data.meta.last_page;
        const visitsInmueblesName = visitsInmueblesTemplateName;
        templatesOptions.value = filteredData.filter(
          (item) =>
            item.prefill_template === false &&
            (hasBauComplementsQuery.value || item.name !== visitsInmueblesName)
        );
        if (isCreate.value) {
          templateSelected.value = filteredData.find((item) => item.prefill_template);
          startAnsweringForm();
        }
        if (isCreateSpecialForm.value) {
          templateSelected.value = filteredData[0];
          if (currentSpecialForm.value === "SITREP") {
            templateSelected.value =
              filteredData.filter((item) => item.name === "SITREP")[0] || [];
          }
          startAnsweringForm();
        }
        if (initialTemplateId != null) {
          const template = data.find((t) => Number(t.id) === Number(initialTemplateId));
          if (template) {
            templateSelected.value = template;
            startAnsweringForm();
          } else {
            // Template not found in paginated results — fetch it directly
            axios
              .get(`/v1/operational-security-forms/templates/${initialTemplateId}`)
              .then((resp) => {
                if (resp.data && resp.data.id) {
                  templateSelected.value = resp.data;
                  startAnsweringForm();
                }
              })
              .catch(() => {});
          }
        }
      })
      .catch(() => {
        toast.error("Error");
      });
  };

  const updateCurrentPage = (direction = "next") => {
    currentPage.value =
      direction === "next" ? currentPage.value + 1 : currentPage.value - 1;
  };

  const onSearch = debounce(function (search) {
    fetchList(search);
  }, 800);

  const nextStep = () => {
    if (currentStep.value < totalSteps) currentStep.value++;
  };

  const checkRiskPercentage = (opt) => {
    return typeof opt?.riskPercentage !== "undefined";
  };

  const setTemplatePreview = () => {
    templatePreview.value = templateSelected.value?.data?.slice(0, 3);
  };

  const startAnsweringForm = () => {
    dataOnSaveForm.value = null;
    isAnsweringForm.value = true;
    facilityName.value = facilitySelected.value?.description;
    templateName.value = templateSelected.value?.name;
    nextStep();
  };

  const getAssignedTaskIdFromQuery = () => {
    const taskIdFromQuery = proxy?.$route?.query?.id_task;
    if (!taskIdFromQuery) return null;
    const parsedTaskId = Number(taskIdFromQuery);
    if (!Number.isFinite(parsedTaskId) || parsedTaskId <= 0) return null;
    return parsedTaskId;
  };

  const completeAssignedTask = async (taskId) => {
    await store.dispatch("unit/updateTaskStatus", {
      id: taskId,
      status: "done",
    });
  };

  const redirectToFormAnswersList = async () => {
    await proxy.$router
      .replace({ path: "/operational_security/form-answers" })
      .catch(() => {});
  };

  const completeAssignedTaskFromQuery = async () => {
    const assignedTaskId = getAssignedTaskIdFromQuery();
    if (!assignedTaskId) return;
    try {
      await completeAssignedTask(assignedTaskId);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || t("messages.error.server_error");
      contentToast(errorMessage, "danger", "AlertTriangleIcon");
    }
  };

  const onAnswersSubmit = async (data) => {
    const formConfig = resolveOperationalSecurityFormConfig(
      data?.template?.name || templateName.value
    );
    dataOnSaveForm.value = data;
    if (data.reference) referenceNumber.value = data.reference;
    if (data.ref) referenceShowed.value = data.reference;
    isFormFromSameUser.value = data?.user?.id === user.id;
    alarmType.value = data?.alarm?.alarm_type;
    currentAlarmId.value = data?.alarm?.id;
    if (data.status) updateCurrentFormStatus(data.status);
    showActionsButtons.value = true;
    if (!evaluation_id.value) {
      getValidators(data.validators, data?.id);
      evaluation_id.value = data.id;
    }

    if (!data?.template?.is_maker_checker && !formConfig.keepOpenOnSave) {
      store.commit("grid/toggleCloseOperationalSecuritFormyModal");
    }
    // The BAU batch reports the combined outcome once at the end.
    if (!isBauBatchInProgress.value) {
      contentToast(
        translations["operational_security.evaluation.answers_successfully"],
        "success",
        "CheckIcon"
      );
    }
    store.dispatch("grid/updateUserConfig");
    updateDataOfSlot(data);

    if (isBauBatchInProgress.value) return;

    if (hasBauComplementsQuery.value && data?.id && currentFormStatus.value !== "completed") {
      await refreshFormStatusById(data.id);
    }

    const assignedTaskId = getAssignedTaskIdFromQuery();
    if (!assignedTaskId) return;

    try {
      await completeAssignedTask(assignedTaskId);
      await redirectToFormAnswersList();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || t("messages.error.server_error");
      contentToast(errorMessage, "danger", "AlertTriangleIcon");
    }
  };

  // function normalizeText(text) {
  //   return text
  //     .toLowerCase()
  //     .normalize('NFD')
  //     .replace(/[\u0300-\u036f]/g, '')
  //     .replace(/\s+/g, ' ')
  //     .trim();
  // }

  const submitError = (error) => {
    const errorMessage =
      error?.error?.response?.data?.message ||
      translations["operational_security.evaluation.answers_error"];
    contentToast(errorMessage, "danger", "AlertTriangleIcon");
  };

  const updateDataOfSlot = (data) => {
    if (formLocation === "modal" || !data) return;
    const currentTabId = store.state.grid.activeTab;
    store.state.grid.tabs[currentTabId].slots.forEach((slot) => {
      if (slot.type === "operational_security_form") {
        if (slot.data.id === data.id_prefill && !slot.data.id_prefill) {
          slot.data = data;
        }
      }
    });
  };

  const setFacilityConfig = () => {
    if (isAnsweringForm.value) {
      disabledUnitSelect.value = true;
      isAnsweringForm.value = false;
      setTimeout(() => {
        isAnsweringForm.value = true;
      }, 50);
    }
    facilityName.value = facilitySelected.value?.description;
  };

  const setReferenceNumber = () => {
    return defaultReferenceNumber;
  };

  function contentToast(message, type, icon = "CheckIcon") {
    toast({
      component: ToastificationContent,
      props: {
        title: message,
        icon,
        variant: type,
      },
    });
  }

  function changeIfValidationRequired(status) {
    buildFormComponent.value._normalizer.isValidationRequired = status;
    buildFormComponent.value?.rebuildDynamicSchema();
  }

  const createActionResult = ({
    action,
    success,
    message,
    data = null,
    error = null,
  }) => ({
    action,
    success,
    message,
    data,
    error,
  });

  const resolveSelectedUserId = () =>
    userSelected.value?.value || userSelected.value?.id || null;

  const resolveFormAnswerId = (evaluation) =>
    dataOnSaveForm.value?.id ||
    currentEvaluation.value?.id ||
    evaluation?.id ||
    evaluation_id.value ||
    null;

  const validateBauNotificationData = ({ selectedUserId, formAnswerId, unitId }) => {
    if (!selectedUserId) {
      return createActionResult({
        action: "notify",
        success: false,
        message: "Seleccione un usuario para notificar",
      });
    }
    if (!formAnswerId) {
      return createActionResult({
        action: "notify",
        success: false,
        message: "No se encontro el formulario para notificar",
      });
    }
    if (!unitId) {
      return createActionResult({
        action: "notify",
        success: false,
        message: "No se encontro el inmueble para notificar",
      });
    }
    return createActionResult({ action: "notify", success: true, message: "" });
  };

  const runApprovalRequest = async ({
    useStatusLock = true,
    showToast = true,
    emitStatusChange = true,
  } = {}) => {
    if (!formValidationData?.id) {
      return createActionResult({
        action: "approve",
        success: false,
        message: "No se encontro validacion para aprobar el formulario",
      });
    }

    if (useStatusLock && isStatusBeingUpdated.value) {
      return createActionResult({
        action: "approve",
        success: false,
        message: translations["operational_security.evaluation.status_error"],
      });
    }

    if (useStatusLock) {
      isStatusBeingUpdated.value = true;
    }

    const payload = {
      id_form_validation: formValidationData.id,
      id_user_validator: user.id,
      validation_comment: validationComment.value,
      id_user_assignment: userSelected.value?.id,
    };

    if (emitStatusChange) {
      EventBus.emit("operational_security_change_status_of_form");
    }
    try {
      const response = await axios.post(
        "/v1/operational-security-forms/form-validation/approve-form",
        payload
      );
      saveSignificantEvent()
      updateCurrentFormStatus(response.data.form.status);
      store.commit("grid/toggleCloseOperationalSecuritFormyModal");
      isFormFromSameUser.value = response?.data?.form?.user.id === user.id;
      const successMessage =
        translations["operational_security.evaluation.status_successfully"];
      if (showToast) {
        contentToast(successMessage, "success", "CheckIcon");
      }
      return createActionResult({
        action: "approve",
        success: true,
        message: successMessage,
        data: response?.data,
      });
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ??
        translations["operational_security.evaluation.status_error"];
      if (showToast) {
        contentToast(errorMessage, "danger", "AlertTriangleIcon");
      }
      return createActionResult({
        action: "approve",
        success: false,
        message: errorMessage,
        error,
      });
    } finally {
      if (useStatusLock) {
        isStatusBeingUpdated.value = false;
      }
    }
  };

  const runRejectRequest = async ({ useStatusLock = true, showToast = true } = {}) => {
    if (!formValidationData?.id) {
      return createActionResult({
        action: "reject",
        success: false,
        message: "No se encontro validacion para rechazar el formulario",
      });
    }

    if (useStatusLock && isStatusBeingUpdated.value) {
      return createActionResult({
        action: "reject",
        success: false,
        message: translations["operational_security.evaluation.status_error"],
      });
    }

    if (useStatusLock) {
      isStatusBeingUpdated.value = true;
    }

    const payload = {
      id_form_validation: formValidationData.id,
      id_user_validator: user.id,
      validation_comment: validationComment.value,
      id_user_assignment: resolveSelectedUserId() || ownerOfForm.value.id,
    };

    EventBus.emit("operational_security_change_status_of_form");
    try {
      const response = await axios.post(
        "/v1/operational-security-forms/form-validation/reject-form",
        payload
      );
      updateCurrentFormStatus(response.data.form.status);
      store.commit("grid/toggleCloseOperationalSecuritFormyModal");
      const successMessage =
        translations["operational_security.evaluation.status_successfully"];
      if (showToast) {
        contentToast(successMessage, "success", "CheckIcon");
      }
      return createActionResult({
        action: "reject",
        success: true,
        message: successMessage,
        data: response?.data,
      });
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ??
        translations["operational_security.evaluation.status_error"];
      if (showToast) {
        contentToast(errorMessage, "danger", "AlertTriangleIcon");
      }
      return createActionResult({
        action: "reject",
        success: false,
        message: errorMessage,
        error,
      });
    } finally {
      if (useStatusLock) {
        isStatusBeingUpdated.value = false;
      }
    }
  };

  const saveBauAsCompleted = async (_evaluation, { showToast = true } = {}) => {
    const submitHandler = buildFormComponent.value?.onSubmit;
    if (typeof submitHandler !== "function") {
      return createActionResult({
        action: "approve",
        success: false,
        message: "No se encontro el formulario para completar",
      });
    }

    try {
      const saveResult = await submitHandler({ markCompleted: true });

      if (saveResult?.skipped) {
        return createActionResult({
          action: "approve",
          success: false,
          message: "El formulario se esta guardando, espere a que termine",
        });
      }

      if (!saveResult?.success) {
        const saveError = saveResult?.error;
        const saveErrorMessage =
          saveError?.response?.data?.message ??
          saveError?.message ??
          "No se pudo completar el formulario";
        if (showToast) {
          contentToast(saveErrorMessage, "danger", "AlertTriangleIcon");
        }
        return createActionResult({
          action: "approve",
          success: false,
          message: saveErrorMessage,
          error: saveError,
        });
      }

      const successMessage = "Formulario completado con exito";
      if (showToast) {
        contentToast(successMessage, "success", "CheckIcon");
      }
      return createActionResult({
        action: "approve",
        success: true,
        message: successMessage,
        data: saveResult.data,
      });
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ??
        translations["operational_security.evaluation.status_error"];
      if (showToast) {
        contentToast(errorMessage, "danger", "AlertTriangleIcon");
      }
      return createActionResult({
        action: "approve",
        success: false,
        message: errorMessage,
        error,
      });
    }
  };

  const completeBauForm = async (evaluation, options = {}) => {
    const saveResult = await saveBauAsCompleted(evaluation, options);
    if (!saveResult.success) return saveResult;

    const formData = await refreshFormStatusById(resolveFormAnswerId(evaluation));
    if (formData?.status === "completed") return saveResult;

    if (!formValidationData?.id) {
      return createActionResult({
        action: "approve",
        success: false,
        message: "El formulario se guardo pero no se pudo marcar como completado",
      });
    }

    const approveResult = await runApprovalRequest({
      ...options,
      emitStatusChange: false,
    });
    if (approveResult.success) {
      await refreshFormStatusById(resolveFormAnswerId(evaluation));
    }
    return approveResult;
  };

  const runApproveFlow = async (evaluation, options = {}) => {
    if (hasBauComplementsQuery.value) {
      return completeBauForm(evaluation, options);
    }
    if (formValidationData?.id) {
      return runApprovalRequest(options);
    }
    return createActionResult({
      action: "approve",
      success: false,
      message: "No se encontro validacion para aprobar el formulario",
    });
  };

  const sendToReview = async (evaluation) => {
    if (isStatusBeingUpdated.value) return;
    changeIfValidationRequired(true);

    const canSave = await buildFormComponent?.value?.validateAndSubmit();
    if (!canSave) {
      contentToast(
        translations["messages.validation.missing_fields.title"],
        "warning",
        "AlertTriangleIcon"
      );
      return;
    }

    isStatusBeingUpdated.value = true;
    const payload = {
      id_form_answer: dataOnSaveForm.value?.id ?? evaluation.id,
      id_user_validator: resolveSelectedUserId(),
    };

    EventBus.emit("operational_security_change_status_of_form");
    axios
      .post("/v1/operational-security-forms/form-validation", payload)
      .then((response) => {
        isStatusBeingUpdated.value = false;
        updateCurrentFormStatus(response.data.form.status);
        store.commit("grid/toggleCloseOperationalSecuritFormyModal");
        contentToast(
          translations["operational_security.evaluation.review_successfully"],
          "success",
          "CheckIcon"
        );
        formValidationData = {
          id: response.data.id,
        };
      })
      .catch((error) => {
        isStatusBeingUpdated.value = false;
        const errorMessage =
          error?.response?.data?.message ??
          translations["operational_security.evaluation.review_error"];
        contentToast(errorMessage, "danger", "AlertTriangleIcon");
      });
  };

  const extractUnitIdFromFormData = (formDataLike) => {
    const data = formDataLike?.data;
    if (!data || typeof data !== "object") return null;

    const directIdEntry = Object.entries(data).find(([key, value]) => {
      if (!/\[id\]$/i.test(String(key || ""))) return false;
      return value != null && value !== "";
    });
    if (directIdEntry) return Number(directIdEntry[1]) || directIdEntry[1];

    const facilityGroupEntry = Object.entries(data).find(([, value]) => {
      return (
        Array.isArray(value) && value.some((item) => item && typeof item === "object")
      );
    });
    if (!facilityGroupEntry) return null;

    const facilityRows = facilityGroupEntry[1];
    const firstRow = facilityRows.find((item) => item && typeof item === "object");
    if (!firstRow) return null;

    const facilityValueEntry = Object.entries(firstRow).find(([key, value]) => {
      if (!/\[(id|ID)\]$/i.test(String(key || ""))) return false;
      return value != null && value !== "";
    });
    if (facilityValueEntry) return Number(facilityValueEntry[1]) || facilityValueEntry[1];

    const facilityObjectEntry = Object.entries(firstRow).find(([, value]) => {
      return (
        value && typeof value === "object" && (value.value != null || value.id != null)
      );
    });
    if (!facilityObjectEntry) return null;
    const nestedId = facilityObjectEntry[1]?.value ?? facilityObjectEntry[1]?.id ?? null;
    return Number(nestedId) || nestedId || null;
  };

  const extractUnitIdFromFacilityRow = (row) => {
    if (!row || typeof row !== "object") return null;

    const directIdEntry = Object.entries(row).find(([key, value]) => {
      if (!/\[(id|ID)\]$/i.test(String(key || ""))) return false;
      return value != null && value !== "";
    });
    if (directIdEntry) return Number(directIdEntry[1]) || directIdEntry[1];

    const facilityObjectEntry = Object.entries(row).find(([, value]) => {
      return (
        value && typeof value === "object" && (value.value != null || value.id != null)
      );
    });
    if (!facilityObjectEntry) return null;

    const nestedId = facilityObjectEntry[1]?.value ?? facilityObjectEntry[1]?.id ?? null;
    return Number(nestedId) || nestedId || null;
  };

  const extractUnitIdFromRawModel = (modelLike) => {
    if (!modelLike || typeof modelLike !== "object") return null;

    const directIdEntry = Object.entries(modelLike).find(([key, value]) => {
      if (!/\[(id|ID)\]$/i.test(String(key || ""))) return false;
      return value != null && value !== "";
    });
    if (directIdEntry) return Number(directIdEntry[1]) || directIdEntry[1];

    const groupEntry = Object.entries(modelLike).find(([, value]) => {
      return (
        Array.isArray(value) && value.some((item) => item && typeof item === "object")
      );
    });
    if (!groupEntry) return null;

    const rows = groupEntry[1];
    for (const row of rows) {
      const rowUnitId = extractUnitIdFromFacilityRow(row);
      if (rowUnitId != null && rowUnitId !== "") return rowUnitId;
    }

    return null;
  };

  const resolveBauUnitIdFromForm = (evaluation) => {
    const formSelectedFacilityId =
      buildFormComponent.value?._normalizer?.lastFacilitySelected?.facility?.value ||
      null;
    if (formSelectedFacilityId)
      return Number(formSelectedFacilityId) || formSelectedFacilityId;

    const fromLiveModel = extractUnitIdFromRawModel(buildFormComponent.value?.model);
    if (fromLiveModel) return fromLiveModel;

    const fromBuildFormUnitId = buildFormComponent.value?.id_unit || null;
    if (fromBuildFormUnitId) return Number(fromBuildFormUnitId) || fromBuildFormUnitId;

    const fromSavedData =
      extractUnitIdFromFormData(dataOnSaveForm.value) ||
      extractUnitIdFromFormData(currentEvaluation.value) ||
      extractUnitIdFromFormData(evaluation);
    if (fromSavedData) return fromSavedData;

    return (
      facilitySelected.value?.id ||
      facilitySelected.value?.value ||
      dataOnSaveForm.value?.id_unit ||
      dataOnSaveForm.value?.unit?.id ||
      dataOnSaveForm.value?.unit?.value ||
      currentEvaluation.value?.id_unit ||
      currentEvaluation.value?.unit?.id ||
      currentEvaluation.value?.unit?.value ||
      evaluation?.id_unit ||
      evaluation?.unit?.id ||
      evaluation?.unit?.value ||
      null
    );
  };

  const resolveBauUnitNameFromForm = (evaluation) => {
    return (
      buildFormComponent.value?._normalizer?.lastFacilitySelected?.facility?.label ||
      dataOnSaveForm.value?.name_unit ||
      currentEvaluation.value?.name_unit ||
      evaluation?.name_unit ||
      dataOnSaveForm.value?.unit?.description ||
      currentEvaluation.value?.unit?.description ||
      evaluation?.unit?.description ||
      ""
    );
  };

  const resolveBauReferenceFromForm = (evaluation) => {
    return (
      dataOnSaveForm.value?.ref ||
      dataOnSaveForm.value?.reference ||
      currentEvaluation.value?.ref ||
      currentEvaluation.value?.reference ||
      evaluation?.ref ||
      evaluation?.reference ||
      ""
    );
  };

  const buildBauNotificationComment = (evaluation) => {
    const unitName = resolveBauUnitNameFromForm(evaluation);
    const referenceNumber = resolveBauReferenceFromForm(evaluation);
    return `Notificacion del inmueble ${unitName} ${referenceNumber}`.trim();
  };

  const executeBauNotificationTask = async (
    evaluation,
    { selectedUserId, formAnswerId, unitId, useStatusLock = true, showToast = true } = {}
  ) => {
    if (useStatusLock && isStatusBeingUpdated.value) {
      return createActionResult({
        action: "notify",
        success: false,
        message: translations["operational_security.evaluation.status_error"],
      });
    }

    const resolvedSelectedUserId = selectedUserId || resolveSelectedUserId();
    const resolvedFormAnswerId = formAnswerId || resolveFormAnswerId(evaluation);
    const resolvedUnitId = unitId || resolveBauUnitIdFromForm(evaluation);
    const validationResult = validateBauNotificationData({
      selectedUserId: resolvedSelectedUserId,
      formAnswerId: resolvedFormAnswerId,
      unitId: resolvedUnitId,
    });

    if (!validationResult.success) {
      if (showToast) {
        contentToast(validationResult.message, "warning", "AlertTriangleIcon");
      }
      return validationResult;
    }

    if (useStatusLock) {
      isStatusBeingUpdated.value = true;
    }

    try {
      const response = await store.dispatch("unit/assignTask", {
        id_user: resolvedSelectedUserId,
        id_form: resolvedFormAnswerId,
        id_unit: resolvedUnitId,
        type: "notification-form",
        comment: buildBauNotificationComment(evaluation),
      });
      const successMessage = response?.message || "Notificacion enviada con exito";
      if (showToast) {
        contentToast(successMessage, "success", "CheckIcon");
      }
      return createActionResult({
        action: "notify",
        success: true,
        message: successMessage,
        data: response,
      });
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ??
        translations["operational_security.evaluation.status_error"];
      if (showToast) {
        contentToast(errorMessage, "danger", "AlertTriangleIcon");
      }
      return createActionResult({
        action: "notify",
        success: false,
        message: errorMessage,
        error,
      });
    } finally {
      if (useStatusLock) {
        isStatusBeingUpdated.value = false;
      }
    }
  };

  const sendBauNotificationTask = async (evaluation) => {
    const result = await executeBauNotificationTask(evaluation, {
      useStatusLock: true,
      showToast: true,
    });

    if (result.success) {
      const formAnswerId = resolveFormAnswerId(evaluation);
      await refreshFormStatusById(formAnswerId);
      EventBus.emit("operational_security_reload_forms_and_list");
    }

    return result;
  };

  const showBatchActionToast = (result, defaultSuccessMessage) => {
    if (result.success) {
      contentToast(result.message || defaultSuccessMessage, "success", "CheckIcon");
      return;
    }
    contentToast(
      result.message || translations["operational_security.evaluation.status_error"],
      "danger",
      "AlertTriangleIcon"
    );
  };

  const notifyAndAcceptBau = async (evaluation) => {
    if (isBauBatchInProgress.value) return;

    const selectedUserId = resolveSelectedUserId();
    const formAnswerId = resolveFormAnswerId(evaluation);
    const unitId = resolveBauUnitIdFromForm(evaluation);
    const validationResult = validateBauNotificationData({
      selectedUserId,
      formAnswerId,
      unitId,
    });
    if (!validationResult.success) {
      contentToast(validationResult.message, "warning", "AlertTriangleIcon");
      return {
        approve: createActionResult({
          action: "approve",
          success: false,
          message: "No se ejecuto aprobacion por datos incompletos",
        }),
        notify: validationResult,
      };
    }

    isBauBatchInProgress.value = true;
    try {
      const approveResult = await runApproveFlow(evaluation, {
        useStatusLock: false,
        showToast: false,
      });

      const refreshedAnswerId = resolveFormAnswerId(evaluation);

      if (!approveResult.success) {
        showBatchActionToast(approveResult, "Formulario completado con exito");
        await refreshFormStatusById(refreshedAnswerId);
        EventBus.emit("operational_security_reload_forms_and_list");
        return {
          approve: approveResult,
          notify: createActionResult({
            action: "notify",
            success: false,
            message: "Notificacion no enviada: el formulario no se completo",
          }),
        };
      }

      const resolvedFormAnswerId = refreshedAnswerId;
      const resolvedUnitId = resolveBauUnitIdFromForm(evaluation);
      const notifyPrecheck = validateBauNotificationData({
        selectedUserId,
        formAnswerId: resolvedFormAnswerId,
        unitId: resolvedUnitId,
      });

      if (!notifyPrecheck.success) {
        contentToast(notifyPrecheck.message, "warning", "AlertTriangleIcon");
        await refreshFormStatusById(resolvedFormAnswerId);
        EventBus.emit("operational_security_reload_forms_and_list");
        return {
          approve: approveResult,
          notify: notifyPrecheck,
        };
      }

      const notifyResult = await executeBauNotificationTask(evaluation, {
        selectedUserId,
        formAnswerId: resolvedFormAnswerId,
        unitId: resolvedUnitId,
        useStatusLock: false,
        showToast: false,
      });

      if (notifyResult.success) {
        contentToast(
          "Formulario completado con exito. Notificacion enviada con exito.",
          "success",
          "CheckIcon"
        );
      } else {
        showBatchActionToast(notifyResult, "Notificacion enviada con exito");
      }

      await refreshFormStatusById(resolveFormAnswerId(evaluation));
      EventBus.emit("operational_security_reload_forms_and_list");

      if (!notifyResult.success) {
        return {
          approve: approveResult,
          notify: notifyResult,
        };
      }

      await completeAssignedTaskFromQuery();
      goBackToList();

      return {
        approve: approveResult,
        notify: notifyResult,
      };
    } finally {
      isBauBatchInProgress.value = false;
    }
  };

  const sendEvaluationFormStatus = async (status) => {
    if (status === "approve") {
      return runApprovalRequest({ useStatusLock: true, showToast: true });
    }
    if (status === "reject") {
      return runRejectRequest({ useStatusLock: true, showToast: true });
    }
    return createActionResult({
      action: status,
      success: false,
      message: translations["operational_security.evaluation.status_error"],
    });
  };

  // const reviewAndValidateForm = (evaluation) => {
  //   if (isStatusBeingUpdated.value) return;
  //   isStatusBeingUpdated.value = true;
  //   const payload = {
  //     id_form_answer: dataOnSaveForm.value?.id ?? evaluation.id,
  //     id_user_validator: userSelected.value?.id
  //   }
  //   axios
  //     .post('/v1/operational-security-forms/form-validation', payload)
  //     .then(response => {
  //       isStatusBeingUpdated.value = false;
  //       updateCurrentFormStatus(response.data.form.status)
  //       store.commit("grid/toggleCloseOperationalSecuritFormyModal");
  //       contentToast(translations["operational_security.evaluation.review_successfully"], "success", "CheckIcon")
  //       formValidationData = {
  //         id: response.data.id
  //       }
  //       sendEvaluationFormStatus("approve")
  //     })
  //     .catch((error) => {
  //       isStatusBeingUpdated.value = false;
  //       const errorMessage = error?.response?.data?.message ?? translations["operational_security.evaluation.review_error"];
  //       contentToast(errorMessage, "danger", "AlertTriangleIcon")
  //     });
  // };

  const reopenForm = (evaluation) => {
    if (isStatusBeingUpdated.value) return;
    isStatusBeingUpdated.value = true;
    const payload = {
      id_form_answer: evaluation.id,
      id_user_reopen: resolveSelectedUserId(),
      reopen_comments: reopenedComment.value,
    };
    axios
      .post("/v1/operational-security-forms/form-validation/reopen-form", payload)
      .then((response) => {
        isStatusBeingUpdated.value = false;
        updateCurrentFormStatus(response.data.form.status);
        store.commit("grid/toggleCloseOperationalSecuritFormyModal");
        contentToast(
          translations["operational_security.evaluation.reopen_successfully"],
          "success",
          "CheckIcon"
        );
        reopenedComments.value = response?.data?.reopened_comments;
        formValidationRejectedAt.value = response?.data?.validation?.rejected_at;
        isFormFromSameUser.value = userSelected.value.id === user.id;
      })
      .catch((error) => {
        isStatusBeingUpdated.value = false;
        const errorMessage =
          error?.response?.data?.message ??
          translations["operational_security.evaluation.reopen_error"];
        contentToast(errorMessage, "danger", "AlertTriangleIcon");
      });
  };

  const updateCurrentFormStatus = (status = null) => {
    currentFormStatus.value = status;
  };

  const onAfterGetTemplate = (data, template) => {
    userAssigned.value = data?.validation?.user;
    templateName.value = template.name;
    showActionsButtons.value = true;
    disabledUnitSelect.value = false;
    templateSelected.value = template;
    formValidationComment.value = data?.validation?.comments;
    reopenedComments.value = data?.validation?.reopened_comments;
    formValidationRejectedAt.value = data?.validation?.rejected_at;
    alarmType.value = data?.alarm?.alarm_type;
    currentAlarmId.value = data?.alarm?.id;
    ownerOfForm.value = data?.owner;
    isOwnerOfForm.value = user.id === ownerOfForm.value?.id;
    significantEventId.value = data?.significantEvent?.id;
    canCloseFormManually.value = template?.is_close_manually_form;
    formValidationAt.value =
      data?.status === "rejected"
        ? data?.validation?.rejected_at
        : data?.validation?.approved_at;
    formValidationData = {
      id: data?.validation?.id,
    };
    if (!currentFormStatus.value || currentFormStatus.value !== data?.status)
      currentFormStatus.value = data?.status || null;
    if (referenceShowed.value === defaultReferenceNumber && data?.reference)
      referenceShowed.value = data.reference;
    if(significantEventId.value) {
      fetchSignificantEvent(significantEventId.value);
    }
    validateShowReferenceNumber(data, template);
    getValidators(data?.validators, data?.id);
    fetchFormHistory(data?.statusHistory);
    if (isFormFromUrl.value) {
      // validateIfUserCanCreateForm(template)
      currentEvaluation.value = data;
      if (referenceShowed.value === defaultReferenceNumber && data?.reference)
        referenceShowed.value = data.reference;
      loadFormData();
      isFormFromUrl.value = false;
    }
  };

  const getValidators = (url, idFormAnswer = '') => {
    let validatorsUrl = url;
    if (!currentFormStatus.value) validatorsUrl = url + "&status=send_to_review";
    if (url) {
      axios
        .get(validatorsUrl, {
          params: {
            id_form_answer: idFormAnswer,
          }
        })
        .then((response) => {
          const formattedOptions = mapUserOptions(response?.data?.data || []);
          validatorsOptions.value = formattedOptions;
          userOptions.value = formattedOptions;
        })
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.message ??
            translations["operational_security.evaluation.status_error"];
          contentToast(errorMessage, "danger", "AlertTriangleIcon");
        });
    }
  };

  const filterUserOption = (option, _, search) => {
    return doesUserOptionMatchSearch({ option, search });
  };

  const refreshFormStatusById = async (formAnswerId) => {
    if (!formAnswerId) return null;
    try {
      const response = await axios.get(
        `/v1/operational-security-forms/template-answers/${formAnswerId}?withData=true&withValidation=true`
      );
      const formData = response?.data || null;
      if (!formData) return null;
      dataOnSaveForm.value = formData;
      currentEvaluation.value = formData;
      if (formData?.validation?.id) {
        formValidationData = { id: formData.validation.id };
      }
      if (formData.status) {
        updateCurrentFormStatus(formData.status);
      }
      fetchFormHistory(formData.statusHistory);
      return formData;
    } catch (_) {
      return null;
    }
  };

  const validateShowReferenceNumber = (evaluation, template) => {
    if (!template) return;
    const folioItem = template.data.find((item) => item.type === "folio");
    referenceShowed.value =
      referenceShowed.value === defaultReferenceNumber && folioItem
        ? replaceTags(folioItem?.options?.fieldTypeCfg?.data[0]?.value)
        : referenceShowed.value;
    showReferenceNumber.value = true;
  };

  function replaceTags(str) {
    if (!str) return "";
    const now = new Date();

    const replacements = {
      "@DD": String(now.getDate()).padStart(2, "0"),
      "@MM": String(now.getMonth() + 1).padStart(2, "0"),
      "@YYYY": String(now.getFullYear()),
      "@YY": String(now.getFullYear()).substring(2),
      "@HH": String(now.getHours()).padStart(2, "0"),
      "@MIN": String(now.getMinutes()).padStart(2, "0"),
      "@INC": "XX",
      "@INC4": "XXXX",
      "@INC6": "XXXXXX",
    };

    for (const tag in replacements) {
      const value = replacements[tag];
      str = str.replace(
        new RegExp(tag, "g"),
        typeof value === "function" ? value() : value
      );
    }

    return str;
  }

  const showAnotherFacility = () => {
    isShowAnotherFacility.value = !isShowAnotherFacility.value;
    anotherFacility.value = "";
  };

  const fetchFormTracking = async (evaluationId) => {
    if (!evaluationId) return;

    isLoadingTracking.value = true;
    try {
      const response = await axios.get(
        `/v1/operational-security-forms/templates-answers/form-tracking/${evaluationId}`
      );
      formTracking.value = response.data.data || [];
    } catch (error) {
      console.error("Error fetching form tracking:", error);
      formTracking.value = [];
    } finally {
      isLoadingTracking.value = false;
    }
  };

  const fetchFormHistory = async (url) => {
    if (!url) return;

    isLoadingHistory.value = true;
    try {
      const response = await axios.get(url);
      formHistory.value = response.data.data || [];
    } catch (error) {
      console.error("Error fetching form history:", error);
      formHistory.value = [];
    } finally {
      isLoadingHistory.value = false;
    }
  };

  const loadFormData = () => {
    if (currentEvaluation.value) {
      isFormFromSameUser.value =
        currentEvaluation.value && currentEvaluation.value?.user?.id === user.id;
      referenceNumber.value = evaluation_id.value
        ? currentEvaluation.value.reference
        : setReferenceNumber();
      referenceShowed.value = evaluation_id.value
        ? currentEvaluation.value.ref
        : setReferenceNumber();
      facilityName.value = currentEvaluation.value?.unit?.description;
      templateName.value = currentEvaluation.value?.template?.name;
      currentFormStatus.value = currentEvaluation.value?.status;
      userOptions.value = validatorsOptions.value;
    }

    if (evaluation_id.value) {
      templateSelected.value = currentEvaluation.value?.template;
      facilitySelected.value = currentEvaluation.value?.unit;
      disabledUnitSelect.value = true;
    }
  };

  const clearQueryParams = () => {
    const currentQueries = { ...(proxy.$route?.query || {}) };
    const hasAlarmQueryParam = Object.prototype.hasOwnProperty.call(
      currentQueries,
      "id_alarm"
    );
    if (!hasAlarmQueryParam) {
      return;
    }
    delete currentQueries.id_alarm;
    proxy.$router.replace({ query: currentQueries }).catch(() => {});
  };

  const validateGoBack = async () => {
    if (
      !isAnsweringForm.value ||
      formStatus.value.isReview ||
      formStatus.value.isCompleted ||
      !isFormFromSameUser.value
    ) {
      clearQueryParams();
      goBackToList();
      return;
    }

    const confirmation = await confirmGoBack();
    if (!confirmation.isConfirmed) return;
    clearQueryParams();
    goBackToList();
  };

  const confirmGoBack = async () => {
    const $swal = proxy?.$swal;

    return await $swal({
      title: translations["operational_security.confirmGoBack"],
      text: translations["operational_security.confirmGoBackSubtitle"],
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: translations["alarm.timeline.yes"],
      cancelButtonText: translations["alarm.timeline.no"],
      customClass: {
        confirmButton: "btn btn-primary text-capitalize",
        cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
      },
      buttonsStyling: false,
    });
  };

  const goBackToList = () => {
    clearForm();
    emit("go-back-to-list");
  };

  function formatUser(s) {
    const name = getUserName(s);
    const email = getUserEmail(s);
    return [name, email].filter(Boolean).join(" | ");
  }

  function getUserName(s) {
    return s?.user_name || s?.user?.name || "";
  }

  function getUserEmail(s) {
    const u = s?.user;
    if (!u) return "";
    if (typeof u === "string") return u;
    return u?.email || "";
  }

  function formatDate(iso) {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  }

  const getStatusLabel = (status) => {
    if (!status) return "-";
    return t(`operational_security.evaluation.status.${status}`);
  };

  const getStatusVariant = (status) => {
    if (!status) return "secondary";
    const statusVariants = {
      in_progress: "primary",
      pending: "warning",
      review: "info",
      completed: "success",
      rejected: "danger",
      reopened: "primary",
    };
    return statusVariants[status] || "secondary";
  };

  const openAlarm = () => {
    const groupUrl = `${window.location.origin}/#/alarm?id_alarm=${currentAlarmId.value}`;
    window.open(groupUrl);
  };

  const getRegionalManagers = () => {
    axios
    .get(`/v1/operational-security-forms/significant-events/users?id_form_answer=${currentEvaluation.value.id}`)
    .then((response) => {
      regionalManagerOptions.value = mapUserOptions(response?.data?.data || []);
    })
  };

  const onChangeIsSignificantEvent = () => {
    if(regionalManagerOptions.value.length || !isSignificantEvent.value) return;

    getRegionalManagers()
  };

  const saveSignificantEvent = () => {
    if(!isSignificantEvent.value) return;

    const payload = {
      significantIdFormAnswer: currentEvaluation.value.id,
      isSignificantEvent: isSignificantEvent.value,
      significantEventTitle: significantEventTitle.value,
      significantEventDescription: significantEventDescription.value,
      significantSecurityManager: significantSecurityManager.value.id
    }

    axios.post('/v1/operational-security-forms/significant-events', payload)
  };

  const saveSignificantEventManagerComments = () => {

    const payload = {
      significantSecurityManagerComments: significantEventDescription.value,
    }

    axios.put(`/v1/operational-security-forms/significant-events/${significantEventId.value}`, payload)
    .then((response) => {
      significantEventDescription.value = null;
      contentToast(translations["operational_security.save_comments_success"], "success");
      significantEventSecManagerComment.value = response?.data?.data?.director_comments
      significantEventSecManagerCommentDate.value = response?.data?.data?.director_commented_at

    })
  };

  const fetchSignificantEvent = (id) => {
    axios
    .get(`/v1/operational-security-forms/significant-events/${id}`)
    .then((response) => {
      significantEventSecManagerComment.value = response?.data?.data?.director_comments
      significantEventSecManagerCommentDate.value = response?.data?.data?.director_commented_at
    })
  };

  const closeForm = () => {
    isStatusBeingUpdated.value = true
    const payload = {
      id_form_answer: currentEvaluation.value.id
    }

    axios.post('/v1/operational-security-forms/form-validation/close-form', payload)
    .then((response) => {
      updateCurrentFormStatus(response.data.status);
      contentToast(translations["operational_security.evaluation.answers_successfully"], "success");
    })
    .catch((e) => {
      contentToast(e.response?.data?.message ?? 'Error on save update for status', "danger", "AlertTriangleIcon");
    })
    .finally(()=> {
      isStatusBeingUpdated.value = false
    })
  };


  return {
    templatesOptions,
    currentPage,
    lastPage,
    currentStep,
    totalSteps,
    templateSelected,
    referenceNumber,
    facilitySelected,
    facilityOptions,
    templatePreview,
    isAnsweringForm,
    facilityName,
    templateName,
    currentFormStatus,
    showActionsButtons,
    formStatus,
    disabledUnitSelect,
    isCreate,
    isPrefill,
    userSelected,
    userOptions,
    validationComment,
    user,
    formValidationComment,
    reopenedComments,
    reopenedComment,
    formValidationRejectedAt,
    formValidationAt,
    isCreateSpecialForm,
    referenceShowed,
    currentSpecialForm,
    anotherFacility,
    isShowAnotherFacility,
    formTracking,
    isLoadingTracking,
    isUserSupervisor,
    showReferenceNumber,
    isFormFromSameUser,
    evaluation_id,
    formHistory,
    isReadOnlyUser,
    isMarkerCheckerFlowAllowed,
    currentEvaluation,
    alarmType,
    canRejectForm,
    canApproveForm,
    canUserReopenForm,
    isStatusBeingUpdated,
    isBauBatchInProgress,
    canUserSendToReview,
    hasBauComplementsQuery,
    isVisitsInmueblesTemplate,
    buildFormComponent,
    isSignificantEvent,
    significantEventTitle,
    significantEventDescription,
    significantSecurityManager,
    regionalManagerOptions,
    significantEventSecManagerComment,
    significantEventSecManagerCommentDate,
    canCloseFormManually,
    ownerOfForm,
    isOwnerOfForm,
    fetchList,
    onSearch,
    updateCurrentPage,
    nextStep,
    checkRiskPercentage,
    setTemplatePreview,
    startAnsweringForm,
    onAnswersSubmit,
    setFacilityConfig,
    setReferenceNumber,
    submitError,
    sendToReview,
    sendBauNotificationTask,
    notifyAndAcceptBau,
    filterUserOption,
    sendEvaluationFormStatus,
    onAfterGetTemplate,
    reopenForm,
    // sendEmailForm,
    showAnotherFacility,
    fetchFormTracking,
    validateIfUrlHasParams,
    clearForm,
    loadFormData,
    goBackToList,
    formatUser,
    getUserName,
    getUserEmail,
    formatDate,
    getStatusLabel,
    getStatusVariant,
    validateGoBack,
    openAlarm,
    onChangeIsSignificantEvent,
    saveSignificantEventManagerComments,
    closeForm
  };
}
